import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useResourceInfoContext } from '@openx/components-core/lib/Form/context/ResourceInfoContext.tsx';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import type { ContextualSegmentsDrawerState } from '@openx/types/targeting/advancedTargeting.ts';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes.ts';
import { noop } from '@openx/utils/noop.ts';
import isEmpty from 'lodash/isEmpty';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { parseCustomFormatToProviderSelections } from '../../ContextualSegments/utils/enrichment.ts';
import { DimensionChip } from '../../shared/DimensionChip/DimensionChip.tsx';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle.tsx';
import { SelectedItems } from '../../shared/SelectedItems/SelectedItems.tsx';
import type { TargetingItemsProps } from '../../types.ts';
import { useGetContextualData } from '../hooks/useGetContextualData.ts';
import { ProviderDetails } from './components/ProviderDetails.tsx';

export interface ContextualSegmentsItemsV2Props extends TargetingItemsProps<ContextualSegmentsDrawerState> {
  accountUid?: string;
  /** Show the empty state; defaults to `!readonly`. */
  showEmptyMessage?: boolean;
}

export const ContextualSegmentsItemsV2 = ({
  targetingParams,
  onChange = noop,
  accountUid = '',
  readonly,
  showEmptyMessage = !readonly,
}: ContextualSegmentsItemsV2Props) => {
  const { t } = useTranslation();
  const { segments, providerName, loading, providerId } = useGetContextualData(accountUid);
  const { contextualV2 } = targetingParams;

  const initProvider = useRef(contextualV2.attr);
  const shouldDisplayProviderAlert = !!initProvider.current && initProvider.current !== contextualV2.attr;

  // Editing covers the account's provider only, but the read-only view lists every provider of the field
  // (e.g. segments of several providers added through the standalone contextual segments card).
  const providerSelections = useMemo(() => {
    const fieldSelections = readonly ? parseCustomFormatToProviderSelections(targetingParams.contextual ?? []) : [];

    return fieldSelections.length
      ? fieldSelections
      : [
          {
            providerId: contextualV2.attr,
            includes: contextualV2.includes ?? [],
            excludes: contextualV2.excludes ?? [],
          },
        ];
  }, [readonly, targetingParams.contextual, contextualV2]);

  const formName = (useResourceInfoContext().name as 'deal' | 'package') || 'package';

  const mappedSegmentsMockOptions = useMemo(
    () =>
      segments.reduce((acc, segment) => {
        acc[segment.id] = segment;

        return acc;
      }, {}),
    [segments]
  );

  const handleRemove = (inclusionKey: 'excludes' | 'includes', remainingSegmentIds: string[]) => {
    onChange(prevValues => ({
      ...prevValues,
      contextualV2: {
        ...prevValues.contextualV2,
        [inclusionKey]: remainingSegmentIds,
      },
    }));
  };

  const removeAllSegments = () => {
    onChange(prevValues => ({
      ...prevValues,
      contextualV2: {
        ...prevValues.contextualV2,
        excludes: [],
        includes: [],
      },
    }));
  };

  const segmentsCount = providerSelections.reduce(
    (count, { includes, excludes }) => count + includes.length + excludes.length,
    0
  );
  const hasSegments = segmentsCount > 0;

  if (loading) {
    return <Loader />;
  }

  if (hasSegments) {
    return (
      <Box data-test="sub-section">
        <FiltersTitle
          onClear={removeAllSegments}
          title={t('Contextual Segments [ {count} ]', {
            count: segmentsCount,
          })}
          readonly={readonly}
        />

        {providerSelections.map(({ providerId: selectionProviderId, includes, excludes }) => {
          // Segment names come from the catalog of the account's provider, so in the read-only view other providers
          // show their segment ids (the same id can name another segment there). Editing covers that provider only.
          const hasCatalogNames = !readonly || selectionProviderId === providerId;
          const getItemLabel = (item: string) => (hasCatalogNames && mappedSegmentsMockOptions[item]?.name) || item;

          return (
            <Box key={selectionProviderId || 'provider'}>
              <ProviderDetails
                providerFromState={selectionProviderId}
                component={formName}
                readonly={readonly}
                providerId={providerId}
                providerName={providerName}
                // The alert is about the provider the drawer edits, so it is shown with that one only.
                shouldDisplayProviderAlert={shouldDisplayProviderAlert && selectionProviderId === contextualV2.attr}
              />

              {!isEmpty(includes) && (
                <SelectedItems
                  dataTest="contains-any-of-segment-ids"
                  filtersTitle={t('Contains any of following Segment IDs')}
                  selectedItems={includes}
                  readonly={readonly}
                  getItemLabel={getItemLabel}
                  isAddIcon
                  handleChange={(segmentIds: string[]) => handleRemove('includes', segmentIds)}
                />
              )}

              {!isEmpty(excludes) && !isEmpty(includes) && (
                <DimensionChip targetingDimension={Criteria.ALL} sx={{ marginLeft: '0' }} />
              )}

              {!isEmpty(excludes) && (
                <SelectedItems
                  dataTest="contains-none-of-segment-ids"
                  filtersTitle={t('Contains none of following Segment IDs')}
                  selectedItems={excludes}
                  getItemLabel={getItemLabel}
                  readonly={readonly}
                  isAddIcon={false}
                  handleChange={(segmentIds: string[]) => handleRemove('excludes', segmentIds)}
                />
              )}
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    !providerId &&
    showEmptyMessage && (
      <Alert severity="warning" sx={{ marginTop: 2, maxWidth: '100%' }}>
        {t(
          'Contextual Segments are not available under this account. To add them, choose an account with connected contextual provider in the On Behalf Of section of the {component} form.',
          { component: formName }
        )}
      </Alert>
    )
  );
};
